import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaUser, FaSignOutAlt, FaLightbulb, FaCertificate, FaCheck, FaTimes, FaClock, FaKey, FaDownload, FaEye, FaFilePdf, FaImage } from 'react-icons/fa';
import ChangePasswordModal from '../components/ChangePasswordModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function UserDashboard() {
  const [userInfo, setUserInfo] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [certificateRequests, setCertificateRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [requestingCertificate, setRequestingCertificate] = useState(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [loadingCertificate, setLoadingCertificate] = useState(false);
  const [selectedRegistrationId, setSelectedRegistrationId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = useCallback(() => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userInfo');
    navigate('/');
  }, [navigate]);

  const loadUserData = useCallback(async (token) => {
    try {
      const [regsResponse, certResponse] = await Promise.all([
        axios.get(`${API_URL}/api/user/registrations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/user/certificate-requests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      setRegistrations(regsResponse.data);
      setCertificateRequests(certResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    const savedUserInfo = localStorage.getItem('userInfo');
    
    if (!token) {
      navigate('/user/login');
      return;
    }

    if (savedUserInfo) {
      setUserInfo(JSON.parse(savedUserInfo));
    }

    // Check if first login
    const params = new URLSearchParams(location.search);
    if (params.get('first_login') === 'true') {
      setShowPasswordModal(true);
    }

    loadUserData(token);
  }, [navigate, location, loadUserData]);

  const handleRequestCertificate = async (registrationId) => {
    const token = localStorage.getItem('userToken');
    setRequestingCertificate(registrationId);

    try {
      await axios.post(
        `${API_URL}/api/user/certificate-request`,
        { registration_id: registrationId },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      alert('✅ درخواست گواهی با موفقیت ارسال شد و پس از بررسی توسط ادمین، گواهی صادر می‌شود');
      // Reload data to show updated certificate status
      await loadUserData(token);
    } catch (error) {
      console.error('Certificate request error:', error);
      const errorMessage = error.response?.data?.detail || 'خطا در ارسال درخواست. لطفاً دوباره تلاش کنید.';
      alert('❌ ' + errorMessage);
    } finally {
      setRequestingCertificate(null);
    }
  };

  const getCertificateStatus = (registrationId) => {
    return certificateRequests.find(req => req.registration_id === registrationId);
  };

  const handleViewCertificate = async (registrationId) => {
    const token = localStorage.getItem('userToken');
    setLoadingCertificate(true);
    setSelectedRegistrationId(registrationId);
    
    try {
      const response = await axios.get(
        `${API_URL}/api/user/certificate-preview/${registrationId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setCertificatePreview(response.data);
      setShowCertificateModal(true);
    } catch (error) {
      console.error('Error loading certificate:', error);
      alert('❌ خطا در بارگذاری گواهی');
    } finally {
      setLoadingCertificate(false);
    }
  };

  const handleDownloadCertificate = async (registrationId, format = 'image') => {
    const token = localStorage.getItem('userToken');
    
    try {
      const response = await axios.get(
        `${API_URL}/api/user/certificate/${registrationId}?format=${format}`,
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${registrationId}.${format === 'pdf' ? 'pdf' : 'jpg'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('❌ خطا در دانلود گواهی');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300 py-20">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center">
                  <FaUser className="text-3xl text-white" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-primary-800 dark:text-primary-400">
                    {userInfo?.first_name} {userInfo?.last_name}
                  </h1>
                  <p className="text-gray-600 dark:text-gray-300">{userInfo?.tech_core_name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{userInfo?.mobile}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <motion.button
                  onClick={() => setShowPasswordModal(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-accent-600 to-accent-500 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  <FaKey />
                  تغییر رمز عبور
                </motion.button>
                <motion.button
                  onClick={handleLogout}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-2 px-5 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold shadow-lg transition-all"
                >
                  <FaSignOutAlt />
                  خروج
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid md:grid-cols-3 gap-6 mb-8"
        >
          <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 text-white shadow-xl">
            <div className="text-4xl font-bold mb-2">{registrations.length}</div>
            <div className="text-white/90">طرح ثبت‌شده</div>
          </div>
          <div className="bg-gradient-to-br from-secondary-600 to-secondary-500 rounded-2xl p-6 text-gray-900 shadow-xl">
            <div className="text-4xl font-bold mb-2">
              {certificateRequests.filter(r => r.status === 'approved').length}
            </div>
            <div className="text-gray-800">گواهی تایید شده</div>
          </div>
          <div className="bg-gradient-to-br from-accent-600 to-accent-500 rounded-2xl p-6 text-white shadow-xl">
            <div className="text-4xl font-bold mb-2">
              {certificateRequests.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-white/90">در انتظار بررسی</div>
          </div>
        </motion.div>

        {/* Registrations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 flex items-center gap-3">
              <FaLightbulb className="text-secondary-500" />
              طرح‌های ثبت‌شده
            </h2>
          </div>

          <div className="p-6">
            {registrations.length === 0 ? (
              <div className="text-center p-12 text-gray-500 dark:text-gray-400">
                <FaLightbulb className="text-6xl mx-auto mb-4 opacity-20" />
                <p className="text-xl">هیچ طرحی ثبت نشده است</p>
              </div>
            ) : (
              <div className="space-y-4">
                {registrations.map((reg) => {
                  const certStatus = getCertificateStatus(reg.id);
                  return (
                    <motion.div
                      key={reg.id}
                      whileHover={{ scale: 1.01 }}
                      className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-600 shadow-lg"
                    >
                      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                            {reg.idea_title}
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <p><span className="font-semibold">دسته:</span> {reg.category_main_name}</p>
                            <p><span className="font-semibold">سطح TRL:</span> {reg.trl_level}</p>
                            <p><span className="font-semibold">تاریخ ثبت:</span> {formatDate(reg.created_at)}</p>
                            <p><span className="font-semibold">وضعیت:</span> {reg.status}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2 min-w-[200px]">
                          {certStatus ? (
                            <>
                              <div className={`flex items-center gap-2 px-4 py-3 rounded-xl font-bold text-sm ${
                                certStatus.status === 'approved' 
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                                  : certStatus.status === 'pending'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                              }`}>
                                {certStatus.status === 'approved' && <><FaCheck /> گواهی تایید شد</>}
                                {certStatus.status === 'pending' && <><FaClock /> در انتظار بررسی</>}
                                {certStatus.status === 'rejected' && <><FaTimes /> درخواست رد شد</>}
                              </div>
                              
                              {/* دکمه‌های مشاهده و دانلود گواهی */}
                              {certStatus.status === 'approved' && (
                                <div className="flex gap-2 mt-2">
                                  <motion.button
                                    onClick={() => handleViewCertificate(reg.id)}
                                    disabled={loadingCertificate && selectedRegistrationId === reg.id}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg font-bold text-sm shadow-md hover:shadow-lg transition-all"
                                    data-testid="view-certificate-btn"
                                  >
                                    <FaEye />
                                    {loadingCertificate && selectedRegistrationId === reg.id ? 'در حال بارگذاری...' : 'مشاهده'}
                                  </motion.button>
                                  <motion.button
                                    onClick={() => handleDownloadCertificate(reg.id, 'image')}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg font-bold text-sm shadow-md hover:shadow-lg transition-all"
                                    title="دانلود تصویر"
                                    data-testid="download-image-btn"
                                  >
                                    <FaImage />
                                  </motion.button>
                                  <motion.button
                                    onClick={() => handleDownloadCertificate(reg.id, 'pdf')}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-lg font-bold text-sm shadow-md hover:shadow-lg transition-all"
                                    title="دانلود PDF"
                                    data-testid="download-pdf-btn"
                                  >
                                    <FaFilePdf />
                                  </motion.button>
                                </div>
                              )}
                            </>
                          ) : (
                            <motion.button
                              onClick={() => handleRequestCertificate(reg.id)}
                              disabled={requestingCertificate === reg.id}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className={`flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-accent-600 to-accent-500 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all ${
                                requestingCertificate === reg.id ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            >
                              <FaCertificate />
                              {requestingCertificate === reg.id ? 'در حال ارسال...' : 'درخواست گواهی'}
                            </motion.button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Certificate Preview Modal */}
      <AnimatePresence>
        {showCertificateModal && certificatePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setShowCertificateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <FaCertificate className="text-green-500" />
                  گواهی حضور
                </h3>
                <button
                  onClick={() => setShowCertificateModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <FaTimes className="text-gray-500" />
                </button>
              </div>
              
              <div className="p-4">
                <div className="mb-4 text-center">
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>نام:</strong> {certificatePreview.full_name}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>عنوان طرح:</strong> {certificatePreview.idea_title}
                  </p>
                </div>
                
                <div className="flex justify-center mb-4">
                  <img 
                    src={certificatePreview.image} 
                    alt="گواهی حضور"
                    className="max-w-full rounded-lg shadow-lg"
                  />
                </div>
                
                <div className="flex justify-center gap-4">
                  <motion.button
                    onClick={() => handleDownloadCertificate(selectedRegistrationId, 'image')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                  >
                    <FaImage />
                    دانلود تصویر
                  </motion.button>
                  <motion.button
                    onClick={() => handleDownloadCertificate(selectedRegistrationId, 'pdf')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                  >
                    <FaFilePdf />
                    دانلود PDF
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={() => {
          setShowPasswordModal(false);
          // Remove first_login query param
          navigate('/user/dashboard', { replace: true });
        }}
      />
    </div>
  );
}

export default UserDashboard;
